import React, { useState } from 'react'
import { MessageCircle, Eye } from 'lucide-react'
import type { ConversationSummary } from '../types/pagination'
import ConversationDetail from './ConversationDetail'

interface ConversationListItemProps {
  conversation: ConversationSummary
  onLoadChat: (phone: string) => void
}

const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  onLoadChat
}) => {
  const [showDetails, setShowDetails] = useState<boolean>(false)

  const formatDate = (dateInput: Date | string): string => {
    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput
    
    if (isNaN(date.getTime())) {
      return 'Fecha inválida'
    }

    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffHours / 24)

    if (diffHours < 1) return 'Hace un momento'
    if (diffHours < 24) return `Hace ${diffHours}h`
    if (diffDays < 7) return `Hace ${diffDays}d`
    return date.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getTagColor = (tag: string): string => {
    const colors: Record<string, string> = {
      interested: '#22c55e',
      contacted: '#3b82f6', 
      contracted: '#8b5cf6',
      not_interested: '#ef4444',
      pending: '#f59e0b'
    }
    return colors[tag] || '#6b7280'
  }

  const displayName = conversation.name && conversation.lastname 
    ? `${conversation.name} ${conversation.lastname}`
    : conversation.name || 'Sin nombre'

  const handleShowDetails = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDetails(true)
  }

  const handleCloseDetails = () => {
    setShowDetails(false)
  }

  const handleLoadChat = (e: React.MouseEvent) => {
    e.stopPropagation()
    onLoadChat(conversation.whatsappPhone)
  }

  return (
    <>
      <div className="conversation-item">
        <div className="col-name">
          <div className="name">{displayName}</div>
          <div className="phone">{conversation.whatsappPhone}</div>
        </div>

        <div className="col-messages">
          {conversation.messageCount}
        </div>

        <div className="col-tools">
          {conversation.toolsUsed}
        </div>

        <div className="col-trips">
          {conversation.tripsConsulted}
        </div>

        <div className="col-last-message">
          {formatDate(conversation.lastMessageAt)}
        </div>

        <div className="col-tag">
          <span 
            className="tag-badge"
            style={{ backgroundColor: getTagColor(conversation.tag) }}
          >
            {conversation.tag}
          </span>
        </div>

        <div className="col-actions">
          <button 
            className="details-button"
            onClick={handleShowDetails}
            title="Ver detalles de conversación"
          >
            <Eye size={16} />
          </button>
          <button 
            className="load-chat-button"
            onClick={handleLoadChat}
            title="Cargar conversación"
          >
            <MessageCircle size={16} />
          </button>
        </div>
      </div>

      {showDetails && (
        <ConversationDetail 
          phone={conversation.whatsappPhone}
          onClose={handleCloseDetails}
        />
      )}
    </>
  )
}

export default ConversationListItem
